/*
 * Pure, IO-free helpers for the Algolia collection engine: Shopify money
 * formatting and the product-hit markup. Kept separate from the engine so the
 * card HTML can mirror `card-product.liquid` / `price.liquid` without dragging
 * InstantSearch wiring along.
 */
(function (theme) {
  'use strict';

  // Shopify's reference money formatter. Operates on integer cents.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || theme.moneyFormat || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultTo(value, fallback) {
      return value == null ? fallback : value;
    }

    function withDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return '0';

      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents = parts[1] ? decimal + parts[1] : '';
      return dollars + cents;
    }

    var value = '';
    switch ((formatString.match(placeholderRegex) || [])[1]) {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'"); break;
      default: value = withDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // Algolia stores prices in major currency units; Shopify's formatter wants cents.
  function money(major) {
    return formatMoney(Math.round(Number(major) * 100), theme.moneyFormat);
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Shopify CDN images accept a width param; request a card-sized render.
  function sizedImage(url, width) {
    if (!url) return '';
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + (width || 450);
  }

  // Price markup from integer cents in the active currency.
  function priceMarkup(price, compare, varies) {
    var onSale = compare > price;
    var current = theme.fromPrice(formatMoney(price), varies);

    var html = '<div class="f-price' + (onSale ? ' f-price--on-sale' : '') + '">';
    html +=
      '<div class="f-price__regular"><span class="f-price-item f-price-item--regular">' +
      (onSale ? '' : current) +
      '</span></div>';
    html += '<div class="f-price__sale">';
    if (onSale) {
      html += '<span class="f-price-item f-price-item--sale">' + current + '</span>';
      html +=
        '<span class="f-price-item f-price-item--regular"><s>' + formatMoney(compare) + '</s></span>';
    }
    html += '</div></div>';
    return html;
  }

  // Indexed prices are shop currency. On another market, emit an empty
  // placeholder the engine fills from that market's product JSON (see
  // `fillLocalizedPrices`) — never the shop-currency figure. Products whose
  // variant prices differ render the indexed "From" price, then get the same
  // refill: the index can't say which compare-at belongs with the lowest price.
  function priceHTML(hit) {
    if (theme.localizedPricing) {
      return (
        '<div class="f-price" data-algolia-price' +
          ' data-handle="' + escapeHtml(hit.handle) + '"></div>'
      );
    }
    var p = theme.hitPrice(hit);
    var html = priceMarkup(Math.round(p.price * 100), Math.round(p.compare_at_price * 100), p.varies);
    if (!p.varies) return html;
    return html.replace('<div class="f-price', '<div data-algolia-price data-handle="' + escapeHtml(hit.handle) + '" class="f-price');
  }

  function fillLocalizedPrices(container) {
    if (!container) return;
    container.querySelectorAll('[data-algolia-price]').forEach(function (el) {
      el.removeAttribute('data-algolia-price');
      var handle = el.getAttribute('data-handle');
      theme.localizedProduct(handle).then(function (product) {
        var p = theme.localizedProductPrice(product);
        if (!el.isConnected) return;
        theme.linkToVariant(el.closest('.product-card'), handle, p.variant);
        el.outerHTML = priceMarkup(p.price, p.compare_at_price, p.varies);
      }).catch(function () {
        // Keep what's there: blank rather than the wrong currency, or the indexed "From" price.
      });
    });
  }

  // Smart Customer Reviews star rating, in the same shape its Shopify app block
  // emits on the product page: the variant SKU identifies the record, and the
  // `item_group` pair rolls every variant's reviews up to the product. The
  // app's script polls for `.stjr-product-rating:not(.stjr-init)` every second,
  // so elements InstantSearch injects on each render get filled in too — as
  // long as the rating bundle was loaded, which the seed element in
  // `algolia-search.liquid` guarantees. Products with no reviews render nothing.
  function reviewsHTML(hit) {
    if (hit.sku == null || hit.id == null) return '';
    return (
      '<div class="algolia-card__reviews">' +
        '<div class="stjr-product-rating"' +
          ' data-product-id="' + escapeHtml(String(hit.sku)) + '"' +
          ' data-group="item_group"' +
          ' data-group-value="' + escapeHtml(String(hit.id)) + '"></div>' +
      '</div>'
    );
  }

  // Badges on Algolia cards, matching the Liquid cards' `pcard-badges`:
  // sold out wins over sale, never both (hit-level prices — same source the
  // price display uses, so badge and slashed price always agree). Then the v1
  // Algolia named-tag label: a `salefromdate:` named tag less than 60 days old
  // wins as "New"; otherwise the first `badge:` named tag (e.g. "Best Seller",
  // "Clearance") renders verbatim. `f-badge--tag` carries the v1 navy colour.
  function badgeHTML(hit) {
    var card = theme.card || {};
    var badges = card.badges || {};
    var labels = card.labels || {};
    var spans = [];

    var soldOut = hit.available === false || hit.inventory_available === false;
    var hitPrice = theme.hitPrice(hit);
    var onSale = hitPrice.compare_at_price > hitPrice.price;
    if (soldOut && badges.showSoldOut !== false) {
      spans.push(
        '<span class="f-badge f-badge--soldout" aria-hidden="true">' +
          escapeHtml(labels.soldOut || 'Sold out') +
        '</span>'
      );
    } else if (onSale && badges.showSale !== false) {
      spans.push(
        '<span class="f-badge f-badge--sale" aria-hidden="true">' +
          escapeHtml(labels.onSale || 'Sale') +
        '</span>'
      );
    }

    var named = hit.named_tags || {};
    var MS_IN_DAY = 1000 * 60 * 60 * 24;

    var saleFrom = named.salefromdate;
    if (Array.isArray(saleFrom)) saleFrom = saleFrom[0];
    var isNew = false;
    if (saleFrom) {
      var from = new Date(saleFrom).getTime();
      isNew = !isNaN(from) && (Date.now() - from) / MS_IN_DAY < 60;
    }

    if (isNew) {
      spans.push('<span class="f-badge f-badge--tag f-badge--new" aria-hidden="true">New</span>');
    } else {
      var badge = named.badge;
      if (Array.isArray(badge)) badge = badge[0];
      if (badge) {
        var slug = String(badge).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        spans.push(
          '<span class="f-badge f-badge--tag f-badge--' + slug + '" aria-hidden="true">' +
            escapeHtml(badge) +
          '</span>'
        );
      }
    }

    if (!spans.length) return '';
    return '<div class="product-card__badge">' + spans.join('') + '</div>';
  }

  // A product hit is one representative variant (distinct grouping), so it only
  // knows its own colour. The full swatch set is fetched + filled in by the
  // engine; here we just emit an empty placeholder carrying what it needs: the
  // product id and which option (option1/2/3) holds the colour.
  function swatchPlaceholder(hit) {
    var names = hit.option_names || [];
    var colorIndex = -1;
    for (var i = 0; i < names.length; i++) {
      if (/colou?r/i.test(String(names[i]))) { colorIndex = i; break; }
    }
    if (colorIndex < 0 || Number(hit.variants_count) < 2) return '';
    return (
      '<div class="algolia-card__swatches" data-algolia-swatches' +
        ' data-product-id="' + escapeHtml(String(hit.id)) + '"' +
        ' data-color-index="' + colorIndex + '"></div>'
    );
  }

  // Quick-add / choose-options button, mirroring the `enable_quick_add` block in
  // `card-product.liquid`. Lives inside `.product-card__image-wrapper` so the
  // theme's existing hover-reveal (desktop) and round-icon (mobile) CSS applies —
  // no new styles needed. Multi-variant products link to the PDP to choose options
  // (the injected card can't host a variant picker); single-variant products get a
  // real `<form is="product-form">` that upgrades to the theme's ProductForm for
  // AJAX add-to-cart + cart drawer.
  function actionsHTML(hit) {
    var card = theme.card || {};
    if (card.enableQuickAdd === false) return '';

    var btnStyle = card.buttonStyle || 'btn--secondary';
    var labels = card.labels || {};
    var icon = card.cartIcon || '';
    var buttonClass = 'product-card__atc product-card__action-button btn ' + btnStyle;

    // Icon shows as the round button on mobile; text shows on desktop — matching
    // the `md:hidden` icon + `product-card__action-text` split in the Liquid card.
    function inner(text) {
      return (
        '<div class="btn__text flex gap-1 items-center">' +
          '<span class="product-card__atc-icon product-card__action-icon inline-flex md:hidden">' + icon + '</span>' +
          '<span class="product-card__atc-text product-card__action-text">' + escapeHtml(text) + '</span>' +
        '</div>'
      );
    }

    if (Number(hit.variants_count) > 1) {
      var url = theme.productUrl(hit.handle);
      return (
        '<div class="product-card__actions product-card__main-actions">' +
          '<a href="' + url + '" class="' + buttonClass + '">' +
            inner(labels.chooseOptions || 'Choose options') +
          '</a>' +
        '</div>'
      );
    }

    // Single-variant: the hit's objectID is the representative (only) variant id,
    // the same value the swatch links use for `?variant=`.
    if (hit.objectID == null) return '';
    var soldOut = hit.available === false || hit.inventory_available === false;
    var text = soldOut ? (labels.soldOut || 'Sold out') : (labels.addToCart || 'Add to cart');
    return (
      '<form is="product-form" action="/cart/add" method="post" enctype="multipart/form-data"' +
        ' class="product-card__actions product-card__main-actions" data-type="add-to-cart-form" novalidate>' +
        '<input type="hidden" name="id" value="' + escapeHtml(String(hit.objectID)) + '"' +
          (soldOut ? ' disabled' : '') + '>' +
        '<button type="submit" name="add"' + (soldOut ? ' disabled' : '') +
          ' class="' + buttonClass + '" aria-label="' + escapeHtml(text) + '">' +
          inner(text) +
        '</button>' +
      '</form>'
    );
  }

  // Mirrors the standard `product-card` markup so Algolia hits render natively.
  function productHitHTML(hit) {
    var title = escapeHtml(hit.title);
    var url = theme.productUrl(hit.handle);
    var image = hit.product_image || hit.image || (hit.image_link || '');

    var media = image
      ? '<img class="motion-reduce" src="' +
        escapeHtml(sizedImage(image, 450)) +
        '" alt="' +
        title +
        '" loading="lazy" width="450" height="450">'
      : '';

    // Algolia coordinates for click/conversion insights (set when
    // `clickAnalytics` is on); read by the delegated listener in
    // `algolia-insights.js`. Absent keys simply omit the attribute.
    var insightsAttrs = '';
    if (hit.objectID != null) {
      insightsAttrs += ' data-algolia-objectid="' + escapeHtml(String(hit.objectID)) + '"';
    }
    if (hit.__queryID) {
      insightsAttrs += ' data-algolia-queryid="' + escapeHtml(String(hit.__queryID)) + '"';
    }
    if (hit.__position != null) {
      insightsAttrs += ' data-algolia-position="' + escapeHtml(String(hit.__position)) + '"';
    }

    return (
      '<div class="product-card product-card-style-standard"' + insightsAttrs + '>' +
        '<div class="product-card__wrapper h-full">' +
          '<div class="product-card__image-wrapper product-card__image-wrapper--main-only">' +
            '<a href="' + url + '" aria-label="' + title + '">' +
              '<div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1">' +
                media +
              '</div>' +
            '</a>' +
            badgeHTML(hit) +
            actionsHTML(hit) +
          '</div>' +
          '<div class="product-card__info">' +
            '<h3 class="product-card__title text-pcard-title">' +
              '<a class="reversed-link block" href="' + url + '">' +
                '<span class="reversed-link__text">' + title + '</span>' +
              '</a>' +
            '</h3>' +
            reviewsHTML(hit) +
            priceHTML(hit) +
            swatchPlaceholder(hit) +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  theme.formatMoney = formatMoney;
  theme.money = money;
  theme.escapeHtml = escapeHtml;
  theme.productHitHTML = productHitHTML;
  theme.fillLocalizedPrices = fillLocalizedPrices;
})((window.AlgoliaTheme = window.AlgoliaTheme || {}));
